import React, { useState, useEffect, useRef } from 'react';
import {
  Building,
  Search,
  MapPin,
  Star,
  Phone,
  Globe,
  Check,
  Loader2,
  ChevronDown,
  ExternalLink,
  AlertCircle
} from 'lucide-react';
import GooglePlacesService from '../utils/googlePlaces';

const BusinessSearchInput = ({
  value,
  onChange,
  onBusinessSelect,
  placeholder = "Search for your business on Google...",
  className = "",
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [showSelectionWarning, setShowSelectionWarning] = useState(false);
  
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Add this useEffect to BusinessSearchInput.jsx
useEffect(() => {
  // Sync searchTerm with external value prop when it changes
  if (value !== searchTerm) {
    setSearchTerm(value || '');
    // Also clear selected business if value is empty
    if (!value) {
      setSelectedBusiness(null);
      setShowSelectionWarning(false);
    }
  }
}, [value, searchTerm]);

  // Get user location on component mount
  useEffect(() => {
    GooglePlacesService.getUserLocation()
      .then(location => setUserLocation(location))
      .catch(err => console.warn('Could not get user location:', err));
  }, []);

  // Handle search with debouncing
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchTerm.length < 2) {
      setSuggestions([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const results = await GooglePlacesService.searchBusinesses(searchTerm, userLocation);
        setSuggestions(results);
      } catch (error) {
        console.error('Search error in component:', error);
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, userLocation]);

  // Handle input blur - validate that user selected from dropdown
  const handleInputBlur = () => {
    // Add delay to allow dropdown clicks to register first
    setTimeout(() => {
      // If user typed something but didn't select from dropdown, show warning
      if (searchTerm.trim() && !selectedBusiness && suggestions.length > 0) {
        // console.log('User typed but did not select from dropdown, showing warning');
        setShowSelectionWarning(true);
        setIsOpen(false);
      } else if (searchTerm.trim() && !selectedBusiness && suggestions.length === 0) {
        // If no suggestions found, allow manual entry
        // console.log('No Google Business Profile found, allowing manual entry');
        setShowSelectionWarning(false);
        setIsOpen(false);
      } else {
        setShowSelectionWarning(false);
        setIsOpen(false);
      }
    }, 200); // 200ms delay to allow dropdown clicks
  };

  // Handle input change
  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    onChange(value);
    
    // Clear selected business and warning when user starts typing again
    if (selectedBusiness) {
      setSelectedBusiness(null);
    }
    setShowSelectionWarning(false);
    
    setIsOpen(true);
  };

  // Handle business selection
  const handleBusinessSelect = async (business) => {
    try {
      // console.log('businessDetails handleBusinessSelect before one :');
      setIsLoading(true);
      // console.log('businessDetails handleBusinessSelect before :');
      // Get detailed business information
      const businessDetails = await GooglePlacesService.getBusinessDetails(business.place_id);
      // console.log('businessDetailshandleBusinessSelect :',businessDetails);
      
      setSelectedBusiness(businessDetails);
      setSearchTerm(businessDetails.businessName);
      onChange(businessDetails.businessName);
      setIsOpen(false);
      
      // Notify parent component
      if (onBusinessSelect) {
        onBusinessSelect(businessDetails);
      }
    } catch (error) {
      console.error('Error selecting business, using fallback:', error);
      // Fallback to basic info
      const fallbackBusiness = {
        businessName: business.name,
        address: business.formatted_address,
        placeId: business.place_id
      };
      
      setSelectedBusiness(fallbackBusiness);
      setSearchTerm(business.name);
      onChange(business.name);
      setIsOpen(false);
      
      if (onBusinessSelect) {
        onBusinessSelect(fallbackBusiness);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !inputRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Render business suggestion item
  const renderBusinessItem = (business) => {
    return (
      <div
        key={business.place_id}
        className="flex items-start space-x-3 p-4 hover:bg-blue-50 cursor-pointer transition-colors border-b border-gray-100 last:border-b-0"
        onClick={() => handleBusinessSelect(business)}
      >
        <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
          <Building className="w-6 h-6 text-blue-600" />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="font-medium text-gray-900 truncate">
                {business.name}
              </h3>
              <p className="text-sm text-gray-500 flex items-center mt-1">
                <MapPin className="w-3 h-3 mr-1" />
                {business.formatted_address}
              </p>
              
              {business.rating && (
                <div className="flex items-center mt-2 space-x-3">
                  <div className="flex items-center space-x-1">
                    <Star className="w-4 h-4 text-yellow-400 fill-current" />
                    <span className="text-sm font-medium text-gray-700">
                      {business.rating}
                    </span>
                    {business.user_ratings_total && (
                      <span className="text-sm text-gray-500">
                        ({business.user_ratings_total})
                      </span>
                    )}
                  </div>
                  
                  {business.business_status === 'OPERATIONAL' && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                      Open
                    </span>
                  )}
                </div>
              )}
            </div>
            
            <ChevronDown className="w-4 h-4 text-gray-400 transform -rotate-90" />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="relative">
      {/* Input Field */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          onBlur={handleInputBlur}
          className={`w-full px-4 py-3 pl-12 pr-4 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
            selectedBusiness ? "border-green-500 bg-green-50" : "border-gray-300"
          } ${className}`}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
        />
        
        <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
          {isLoading ? (
            <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
          ) : (
            <Search className="w-4 h-4 text-gray-400" />
          )}
        </div>
        
        {selectedBusiness && (
          <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
            <Check className="w-5 h-5 text-green-500" />
          </div>
        )}
      </div>

      {/* Warning message when user types but doesn't select from dropdown */}
      {showSelectionWarning && searchTerm.trim() && !selectedBusiness && (
        <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start space-x-2">
            <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-amber-800 font-medium">
                Please select from Google Business Profile list
              </p>
              <p className="text-xs text-amber-700 mt-1">
                To ensure accurate business information, please select your business from the dropdown list above, or clear the field and add website address below.
              </p>
              <button
                type="button"
                onClick={() => {
                  setSearchTerm('');
                  onChange('');
                  setShowSelectionWarning(false);
                }}
                className="mt-2 text-xs text-amber-600 hover:text-amber-800 underline"
              >
                Clear field
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Message for invalid/nonsense input with no results */}
      {searchTerm.trim() && searchTerm.length >= 2 && !isLoading && suggestions.length === 0 && !selectedBusiness && !showSelectionWarning && (
        <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start space-x-2">
            <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-blue-800 font-medium">
                No Google Business Profile found
              </p>
              <p className="text-xs text-blue-700 mt-1">
                We couldn't find "{searchTerm}" in Google Business listings. You can clear the field and add your website address below, or try a different search term.
              </p>
              <button
                type="button"
                onClick={() => {
                  setSearchTerm('');
                  onChange('');
                }}
                className="mt-2 text-xs text-blue-600 hover:text-blue-800 underline"
              >
                Clear field
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Selected Business Info - Clean display without heading */}
      {selectedBusiness && (
        <div className="mt-3 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
              <Check className="w-4 h-4 text-green-600" />
            </div>
            
            <div className="flex-1 min-w-0">
              {/* Business Name */}
              <h4 className="font-medium text-green-900">
                {selectedBusiness.businessName}
              </h4>
              
              {/* Complete Formatted Address */}
              {selectedBusiness.address && (
                <div className="mt-2">
                  <div className="flex items-start space-x-2">
                    <MapPin className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-green-700 leading-relaxed break-words">
                      {selectedBusiness.address}
                    </p>
                  </div>
                </div>
              )}
              
 
            </div>
          </div>
        </div>
      )}

      {/* Dropdown with suggestions */}
      {isOpen && (searchTerm.length >= 2) && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-y-auto"
        >
          {isLoading ? (
            <div className="p-4 text-center">
              <Loader2 className="w-6 h-6 text-blue-500 animate-spin mx-auto mb-2" />
              <p className="text-sm text-gray-500">Searching Google Business Profiles...</p>
            </div>
          ) : suggestions.length > 0 ? (
            <div>
              <div className="p-3 bg-blue-50 border-b border-blue-100">
                <p className="text-sm font-medium text-blue-900">
                  Google Business Profiles
                </p>
                <p className="text-xs text-blue-700">
                  Select your verified business listing
                </p>
              </div>
              
              {suggestions.map(renderBusinessItem)}
            </div>
          ) : (
            <div className="p-4 text-center">
              <Building className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">
                No Google Business Profiles found for "{searchTerm}"
              </p>
              <p className="text-xs text-gray-400 mt-1">
                You can clear the field and add your website address below
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BusinessSearchInput;